'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';


interface NavigationItem {
  label: string;
  path: string;
}

const Header = () => {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Available Puppies', path: '/available-puppies' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Testimonials', path: '/customer-testimonials' },
    { label: 'Health Guarantee', path: '/health-guarantee' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact-us' },
  ];

  const isActivePath = (path: string) => {
    return pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-[100] bg-card shadow-warm transition-smooth">
      <div className="container mx-auto">
        <div className="flex items-center justify-between py-5">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center gap-3 transition-smooth hover:opacity-80"
            onClick={closeMobileMenu}
          >
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-primary">
              <AppImage
                src="/assets/images/lap_dog-1769787911131-1769795751108.jpg"
                alt="Luminex Labradors Logo - Labrador Dog"
                width={48}
                height={48}
                priority={true}
                quality={90}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-heading text-xl font-bold leading-tight text-foreground">
                LuminexLabradors
              </span>
              <span className="caption text-xs text-muted-foreground">
                Quality Labrador Breeders
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:block">
            <ul className="flex items-center gap-8">
              {navigationItems.map((item) => (
                <li key={item.path}>
                  <Link
                    href={item.path}
                    className={`font-body text-base font-medium transition-smooth hover:text-primary ${
                      isActivePath(item.path)
                        ? 'text-primary' :'text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            onClick={toggleMobileMenu}
            className="flex h-11 w-11 items-center justify-center rounded-md text-foreground transition-smooth hover:bg-muted focus-ring lg:hidden"
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon 
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} 
              size={24} 
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="border-t border-border pb-5 lg:hidden">
            <ul className="flex flex-col gap-1 pt-4">
              {navigationItems.map((item) => (
                <li key={item.path}>
                  <Link
                    href={item.path}
                    onClick={closeMobileMenu}
                    className={`block rounded-md px-4 py-3 font-body text-base font-medium transition-smooth hover:bg-muted ${
                      isActivePath(item.path)
                        ? 'bg-primary/10 text-primary' :'text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;